import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { Image, Upload } from 'antd';
import type { GetProp, UploadFile, UploadProps } from 'antd';
import React, { useEffect, useState } from 'react';

import { openNotification } from '../../common/helpers';
import { NotificationTypes } from '../../common/interfaces';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import { DocTypeClass } from '../../pages/Registration/Registration.interfaces';
import styles from './UploadImg.module.scss';

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

const getBase64 = (file: FileType): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

type Props = {
  uploadImg: any;
  onRemoveImg: any;
  isLoading?: boolean;
  imgBlob?: string | null;
  onSetImg?: (imgBlob: string) => void;
};
export const UploadImg: React.FC<Props> = ({
  imgBlob,
  isLoading,
  uploadImg,
  onRemoveImg,
}) => {
  const { documents } = useTypedSelector((state) => state.registration);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const beforeUpload = async (file: FileType) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async () => {
      const base64StringMIME = reader.result as string;
      const base64String = base64StringMIME.split(',')[1];

      // Так как селфи и документы грузятся по одному методу, вбиваем пустые поля
      const preparedDataImg = {
        fileName: 'Фото (селфи) с паспортом' + '.' + file.name.split('.').pop(),
        number: '-',
        date: '',
        validTo: '',
        comment: '',
        docTypeClass: DocTypeClass.PHOTO,
        doctypeId:
          documents.find((item) => item.docTypeClass === DocTypeClass.PHOTO)
            ?.id ?? '',
        data: base64String,
      };

      try {
        await uploadImg(preparedDataImg).unwrap();
        openNotification(
          NotificationTypes.SUCCESS,
          'Документ успешно загружен на сервер',
        );
      } catch (e) {
        openNotification(
          NotificationTypes.ERROR,
          `Произошла ошибка при загрузке документа. Попробуйте ещё раз.`,
        );
        console.error(e);
        setFileList([]);
      }
    };
    return false;
  };

  const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as FileType);
    }

    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };

  const uploadButton = (
    <button style={{ border: 0, background: 'none' }} type="button">
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Загрузить фото</div>
    </button>
  );

  useEffect(() => {
    if (imgBlob) {
      setFileList([
        {
          // Тип объекта для antd, нужен только "url"
          uid: '-1',
          name: 'Фото (селфи) с паспортом',
          status: 'done',
          url: imgBlob,
          linkProps: '{"download": "image"}',
        },
      ]);
    }
  }, [imgBlob]);

  return (
    <div>
      {isLoading ? (
        <div className={styles.loader}>
          <LoadingOutlined />
        </div>
      ) : (
        <Upload
          listType="picture-card"
          fileList={fileList}
          onPreview={handlePreview}
          onChange={handleChange}
          maxCount={1}
          beforeUpload={beforeUpload}
          onRemove={onRemoveImg}
        >
          {fileList.length >= 1 || isLoading ? null : uploadButton}
        </Upload>
      )}

      {previewImage && (
        <Image
          wrapperStyle={{ display: 'none' }}
          preview={{
            visible: previewOpen,
            onVisibleChange: (visible) => setPreviewOpen(visible),
            afterOpenChange: (visible) => !visible && setPreviewImage(''),
          }}
          src={previewImage}
        />
      )}
    </div>
  );
};
